import {Component, HostListener, OnDestroy, OnInit} from '@angular/core';
import {HttpErrorResponse} from '@angular/common/http';
import {Observable, Subject, Subscription, timer} from 'rxjs';
import {map, takeUntil, tap} from 'rxjs/operators';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {StateService} from '@uirouter/angular';
import {NewDataService} from '../../original-services/new-data.service';
import {ButtonStyle} from '../../components/ui/text-button/text-button.component';
import {RoutingService} from '../../original-services/routing.service';
import {PASSWORD_PATTERN} from '../../common/patterns';
import {LoggerService} from '../../original-services/logger.service';
import {STATE_NAMES} from '../../common/state-names';
import {AuthenticationService} from '../../original-services/authentication.service';
import {PostLoginNavigationService} from 'app/services/post-login-navigation.service';
import {AppShellPrefetchService} from 'app/services/app-shell/app-shell-prefetch.service';

type ActivationLinkStatus = 'checking' | 'valid' | 'invalid' | 'active';

type PasswordScreen =
    | {kind: 'invite'}
    | {kind: 'activate'; status: ActivationLinkStatus; email: string};

@Component({
    selector: 'page-new-password',
    templateUrl: './new-password.component.html',
    styleUrls: ['./new-password.component.scss'],
    standalone: false
})
export class NewPasswordPageComponent implements OnInit, OnDestroy {
    token: string;
    uid: string;
    buttonStyle = ButtonStyle;
    submitPressed = false;
    isSubmitting = false;
    errorMessage?: string;
    successMessage?: string;
    resent = false;
    screen: PasswordScreen = {kind: 'invite'};
    emailCtrl = new FormControl('');
    signInState = STATE_NAMES.login;
    formGroup: FormGroup<{new_password: FormControl<string | null>}>;

    private submitPassword: (password: string) => Observable<void> = () => {
        throw new Error('Password screen was not chosen');
    };
    private enterSub?: Subscription;
    private readonly destroy$ = new Subject<void>();

    get newPassword(): FormControl<string | null> {
        return this.formGroup.controls.new_password;
    }

    get email(): string {
        switch (this.screen.kind) {
            case 'invite':
                return '';
            case 'activate':
                return this.screen.email;
            default: {
                const exhaustive: never = this.screen;
                return exhaustive;
            }
        }
    }

    get headingKey(): string {
        if (this.screen.kind === 'invite') {
            return 'NewPassword.Title';
        }
        return this.screen.status === 'active' ? 'NewPassword.AlreadyActiveTitle' : 'NewPassword.ActivateTitle';
    }

    get showPasswordForm(): boolean {
        if (this.successMessage) {
            return false;
        }
        switch (this.screen.kind) {
            case 'invite':
                return true;
            case 'activate':
                return this.screen.status === 'valid';
            default: {
                const exhaustive: never = this.screen;
                return exhaustive;
            }
        }
    }

    constructor(
        private routingService: RoutingService,
        private fb: FormBuilder,
        private newDataService: NewDataService,
        private logger: LoggerService,
        private stateService: StateService,
        private authService: AuthenticationService,
        private postLogin: PostLoginNavigationService,
        private appShellPrefetch: AppShellPrefetchService
    ) {
        this.uid = this.routingService.params['param1'];
        this.token = this.routingService.params['param2'];
        this.formGroup = this.fb.group({
            new_password: new FormControl<string | null>(null, [
                Validators.required,
                Validators.minLength(6),
                Validators.pattern(PASSWORD_PATTERN)
            ])
        });

        this.newPassword.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.errorMessage = undefined;
        });
        // Chrome's password manager skips type="hidden", readonly, and display:none username
        // fields. This stays a normal editable autocomplete="username" input, clipped out of
        // view, and any edit snaps back to the activation address.
        this.emailCtrl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => {
            if (this.email && value !== this.email) {
                this.emailCtrl.setValue(this.email, {emitEvent: false});
            }
        });
    }

    ngOnInit() {
        const data = this.stateService.current?.data as {mode?: string} | undefined;
        if (data?.mode === 'activate') {
            this.screen = {kind: 'activate', status: 'checking', email: ''};
            this.submitPassword = password => this.activate(password);
            this.checkActivationLink();
            return;
        }
        this.screen = {kind: 'invite'};
        this.submitPassword = password => this.resetInvitePassword(password);
    }

    @HostListener('document:keydown.enter')
    onKeyEnter() {
        this.createPassword();
    }

    createPassword() {
        if (this.isSubmitting || this.successMessage || !this.showPasswordForm) {
            return;
        }
        this.submitPressed = true;
        const password = this.newPassword.value;
        if (!this.formGroup.valid || !password) {
            return;
        }
        this.errorMessage = undefined;
        this.isSubmitting = true;
        this.submitPassword(password).pipe(takeUntil(this.destroy$)).subscribe({
            error: (error: HttpErrorResponse) => {
                this.isSubmitting = false;
                this.logger.error(error);
                this.errorMessage = 'NewPassword.Form.ErrorDefault';
            }
        });
    }

    resend() {
        if (this.screen.kind !== 'activate' || this.isSubmitting || this.resent) {
            return;
        }
        this.isSubmitting = true;
        this.authService.resendActivation(this.uid).pipe(takeUntil(this.destroy$)).subscribe({
            next: () => {
                this.isSubmitting = false;
                this.resent = true;
            },
            error: (error: HttpErrorResponse) => {
                this.isSubmitting = false;
                this.logger.error(error);
                if (error?.error?.code === 'already_active') {
                    this.screen = {kind: 'activate', status: 'active', email: this.email};
                    return;
                }
                this.errorMessage = 'NewPassword.Form.ErrorDefault';
            }
        });
    }

    /**
     * Activation logs the user straight into the app, so typing into or pressing on the password field
     * there is the same intent signal as on sign-in: warm the authenticated chunk while they type.
     * Bound to `input`/`pointerdown`, not `focusin`: the field carries `appAutofocus`, so a focus
     * trigger fired on render and pulled the whole app shell with no user action. Invite mode is left
     * alone — it ends on the sign-in page, which warms the chunk itself.
     */
    onFieldInteraction(event: Event): void {
        const target = event.target as HTMLElement | null;
        if (this.screen?.kind === 'activate' && target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) {
            this.appShellPrefetch.warm();
        }
    }

    redirectToSingIn() {
        this.routingService.go(STATE_NAMES.login);
    }

    ngOnDestroy() {
        this.enterSub?.unsubscribe();
        this.destroy$.next();
        this.destroy$.complete();
    }

    private checkActivationLink() {
        this.authService.checkActivationLink(this.uid, this.token).pipe(takeUntil(this.destroy$)).subscribe({
            next: (body: {email?: string} | null) => {
                const email = body?.email || '';
                this.screen = {kind: 'activate', status: 'valid', email};
                this.emailCtrl.setValue(email);
            },
            error: (error: HttpErrorResponse) => {
                const status = error?.error?.code === 'already_active' ? 'active' : 'invalid';
                this.screen = {kind: 'activate', status, email: ''};
                this.logger.error(error);
            }
        });
    }

    private activate(password: string): Observable<void> {
        return this.authService.activateTenant(this.uid, this.token, password).pipe(
            tap(data => {
                this.authService.establishSession(data);
                this.enterSub?.unsubscribe();
                this.enterSub = this.postLogin.enterApp(null).pipe(takeUntil(this.destroy$)).subscribe();
            }),
            map(() => undefined)
        );
    }

    private resetInvitePassword(password: string): Observable<void> {
        return this.newDataService.createPassword({
            new_password: password,
            uidb64: this.uid,
            token: this.token
        }).pipe(
            tap(() => {
                this.isSubmitting = false;
                this.successMessage = 'NewPassword.Form.Success';
                timer(3000).pipe(takeUntil(this.destroy$)).subscribe(() => this.redirectToSingIn());
            }),
            map(() => undefined)
        );
    }
}
